import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import WorkbenchShell from "../WorkbenchShell";
import { useWorkbench } from "../WorkbenchContext";
import JobContentsRail from "./JobContentsRail";
import LineItemEditor from "./LineItemEditor";
import JobCostsEditor from "./JobCostsEditor";
import CostTraceInspector from "./CostTraceInspector";
import EstimateStatusStrip from "./EstimateStatusStrip";
import ComposerDialog from "./ComposerDialog";
import { useComposerAvailability } from "./useComposer";
import { buildCandidatePayload, normalizeDraft } from "./composerModel";
import { isBlankLineItem } from "../../../state/lineItems";
import { toNumberOrNull } from "./qboLine";
import {
  fileRateCardCandidate,
  filePriceObservation,
} from "../../../services/apiService";
import {
  CLIENT_PRICING_TIER_OPTIONS,
  getPricingTierLabel,
} from "../../../utils/pricingTemplates";
import { formatCurrency } from "../../../utils/formatters";
import "./estimate.css";

/**
 * The estimate workbench.
 *
 *   rail      what is in this job
 *   centre    the line you are editing
 *   inspector where its number came from
 *
 * This component owns no pricing state. It reads `formData` (the reducer) and
 * `results` (the backend), and dispatches the same actions the old table
 * dispatched. Which line is selected and whether the trace is scoped to the
 * line or the whole job live in the shared workbench context, so they survive
 * navigating away and back.
 */

function formatStatus(status) {
  if (!status || typeof status !== "string") return "Draft";
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * A problem indicator, not a coaching line.
 *
 * Every string here names an abnormal state the estimator has to clear. The
 * steady states — an unsaved job, a priced job ready to send — return null so
 * the strip spends no cell telling someone what they already know for a whole
 * session. `resultsStale` is not handled here either: the strip owns that case
 * and renders `Pricing / Recalculate` in its place.
 */
function nextActionTitle({
  hasDataIssues,
  isDataLoading,
  isCalculating,
  calculationError,
  results,
}) {
  if (hasDataIssues) return "Resolve materials loading";
  if (isDataLoading) return "Preparing estimate data";
  if (isCalculating) return "Calculating live pricing";
  if (calculationError) return "Review calculation issue";
  if (!results) return "Build the first priced view";
  return null;
}

function TierControl({ formData, dispatch }) {
  const [justChanged, setJustChanged] = useState(false);

  useEffect(() => {
    if (!justChanged) return undefined;
    const timer = window.setTimeout(() => setJustChanged(false), 4000);
    return () => window.clearTimeout(timer);
  }, [justChanged]);

  const tier =
    formData.client_pricing_tier ||
    formData.default_client_pricing_tier ||
    "retail";

  const consequence = `Switching tier re-seeds overhead, profit, tax and commission from the ${getPricingTierLabel(
    tier,
  )} template`;

  return (
    <div className="est-tier">
      <label className="est-tier__label" htmlFor="est-tier-select">
        Tier
      </label>
      <select
        id="est-tier-select"
        className="est-tier__select"
        value={tier}
        aria-label="Client pricing tier"
        title={consequence}
        onChange={(event) => {
          setJustChanged(true);
          dispatch({
            type: "UPDATE_FIELD",
            field: "client_pricing_tier",
            value: event.target.value,
          });
        }}
      >
        {CLIENT_PRICING_TIER_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {/* Floated out of the 46px bar so a long sentence can never push the
          chrome around; it appears on hover/focus and for a beat after a
          change, so nobody re-prices a job without being told. */}
      <span
        className={`est-tier__note${justChanged ? " est-tier__note--on" : ""}`}
        role="status"
      >
        {consequence}
      </span>
    </div>
  );
}

function EstimateWorkbench({
  formData,
  dispatch,
  currentJob = null,
  viewingVersionNumber = null,
  results = null,
  resultsStale = false,
  isCalculating = false,
  calculationError = "",
  onCalculate,
  onSaveClick,
  onNewJobClick,
  onShowVersionHistory,
  onLoadLatest,
  isAdmin = false,
}) {
  const { selectedLineIndex, setSelectedLineIndex, traceScope, setTraceScope } =
    useWorkbench();

  const laborPanelRef = useRef(null);
  const tierRef = useRef(null);

  const lineItems = useMemo(
    () => (Array.isArray(formData?.line_items) ? formData.line_items : []),
    [formData?.line_items],
  );
  const extrasIndex = lineItems.length;

  // A removed line must not leave the centre pane pointing at nothing.
  useEffect(() => {
    if (selectedLineIndex > extrasIndex) setSelectedLineIndex(extrasIndex);
  }, [selectedLineIndex, extrasIndex, setSelectedLineIndex]);

  const activeIndex = Math.min(selectedLineIndex, extrasIndex);
  const showingExtras = activeIndex === extrasIndex;
  const activeLine = showingExtras ? null : lineItems[activeIndex] || null;
  const activeResult = showingExtras
    ? null
    : results?.line_item_results?.[activeIndex] || null;

  const handleAddLine = useCallback(() => {
    dispatch({ type: "ADD_LINE_ITEM" });
    setSelectedLineIndex(lineItems.length);
  }, [dispatch, lineItems.length, setSelectedLineIndex]);

  /* ── AI line composer ───────────────────────────────────────────────────
   * The line itself carries only its provenance stamp. The bulky review
   * material — rationale, cited evidence, per-field confidence, the rate-card
   * candidate — lives here in component state, keyed by draft_id rather than
   * by index so reordering or removing a line can never leave the inspector
   * showing someone else's reasoning. It deliberately does not persist into
   * the JobVersion: the permanent record is the badge, the numbers and the
   * provenance stamp.
   */
  const composer = useComposerAvailability();
  const [composerOpen, setComposerOpen] = useState(false);
  const [draftsById, setDraftsById] = useState({});
  const [candidateStateById, setCandidateStateById] = useState({});

  const activeDraftId = activeLine?.ai_drafted?.draft_id || null;
  const activeDraft = activeDraftId ? draftsById[activeDraftId] || null : null;

  const handleDrafted = useCallback(
    (payload) => {
      const draft = normalizeDraft(payload);
      // Landing a draft is an edit like any other: the results go stale and
      // the estimator presses Calculate. Nothing here prices anything.
      dispatch({ type: "INSERT_AI_DRAFT_LINE_ITEM", lineItem: draft.lineItem });
      if (draft.draftId) {
        setDraftsById((current) => ({ ...current, [draft.draftId]: draft }));
      }
      // Mirrors the reducer's replace-the-lone-blank-line rule so the centre
      // pane lands on the draft rather than one row past it.
      const replacesBlank =
        lineItems.length === 1 && isBlankLineItem(lineItems[0]);
      setSelectedLineIndex(replacesBlank ? 0 : lineItems.length);
      setComposerOpen(false);
    },
    [dispatch, lineItems, setSelectedLineIndex],
  );

  /**
   * File the devised rate-card candidate an accepted draft implies.
   *
   * Non-blocking and non-fatal by design: the line is already accepted, and a
   * failed bookkeeping POST must never roll that back. Worst case the owner
   * doesn't get a candidate to review and the estimator sees a Retry.
   */
  const fileCandidate = useCallback(
    (draft) => {
      const payload = buildCandidatePayload(draft, {
        jobName: formData.job_name || currentJob?.job_name || null,
        lineName: draft.lineItem?.name || null,
      });
      if (!payload) return;
      fileRateCardCandidate(payload)
        .then(() =>
          setCandidateStateById((current) => ({
            ...current,
            [draft.draftId]: "filed",
          })),
        )
        .catch(() =>
          setCandidateStateById((current) => ({
            ...current,
            [draft.draftId]: "failed",
          })),
        );
    },
    [formData.job_name, currentJob?.job_name],
  );

  const handleAcceptDraft = useCallback(() => {
    dispatch({ type: "ACCEPT_AI_DRAFT_LINE_ITEM", index: activeIndex });
    if (activeDraft?.rateCardCandidate?.family_uncarded === true) {
      fileCandidate(activeDraft);
    }
  }, [dispatch, activeIndex, activeDraft, fileCandidate]);

  const handleRetryCandidate = useCallback(() => {
    if (activeDraft) fileCandidate(activeDraft);
  }, [activeDraft, fileCandidate]);

  /* ── Quoted price (Goal 2b) ─────────────────────────────────────────────
   * Mirrors the AI-candidate filing pattern exactly: non-blocking,
   * non-fatal, and never rolls back the edit that produced it. Filed/failed
   * state is keyed by line index (not draft id — a quoted price has no
   * draft) and deliberately does not persist into the JobVersion; it exists
   * only to give the estimator "Filed for review" / "Couldn't file — Retry"
   * feedback while this pane is open.
   */
  const [quotedPriceStateByIndex, setQuotedPriceStateByIndex] = useState({});

  const handleFileQuotedPrice = useCallback(
    (lineIndex) => {
      const line = lineItems[lineIndex];
      if (!line) return;
      const result = results?.line_item_results?.[lineIndex] || null;
      const payload = {
        category: line.product_type_key,
        observed_price: line.material_price_override,
        basis: line.material_price_override_basis || "per_unit",
        quantity: line.quantity,
        sqft_each: result?.calculated_sqft ?? null,
        // A product type that does not use a dimension (e.g. no length on a
        // shutter) clears it to "" so it never rides into the calculate
        // payload — the observation endpoint's Float fields reject "" as
        // "Not a valid number", so it must go over the wire as null instead.
        dimensions: {
          length_in: toNumberOrNull(line.length),
          width_in: toNumberOrNull(line.width),
          height_in: toNumberOrNull(line.height),
        },
        material_class: line.material_classification || null,
        rate_card_id: result?.rate_card_resolved?.card_id ?? null,
        context: {
          job_name: formData.job_name || currentJob?.job_name || null,
          line_name: line.name || null,
          parent_line_name: null,
          note: line.material_price_override_note || null,
        },
      };
      filePriceObservation(payload)
        .then(() =>
          setQuotedPriceStateByIndex((current) => ({
            ...current,
            [lineIndex]: "filed",
          })),
        )
        .catch(() =>
          setQuotedPriceStateByIndex((current) => ({
            ...current,
            [lineIndex]: "failed",
          })),
        );
    },
    [lineItems, results, formData.job_name, currentJob?.job_name],
  );

  const handleDiscardDraft = useCallback(() => {
    const draftId = activeDraftId;
    dispatch({ type: "DISCARD_AI_DRAFT_LINE_ITEM", index: activeIndex });
    if (draftId) {
      setDraftsById((current) => {
        const { [draftId]: _dropped, ...rest } = current;
        return rest;
      });
    }
    setSelectedLineIndex(Math.max(0, activeIndex - 1));
  }, [dispatch, activeIndex, activeDraftId, setSelectedLineIndex]);

  // One action, not four zeroed fields — a typed zero is still an override and
  // would price the line with no shop labor at all.
  /**
   * Duplicate lands you on the copy, not on the original.
   *
   * The reducer splices the clone in directly after its source, so the new
   * line is always `activeIndex + 1`. Staying put made Duplicate read as "did
   * nothing" and produced silent double-edits of the original.
   */
  const handleDuplicateLine = useCallback(() => {
    dispatch({ type: "DUPLICATE_LINE_ITEM", index: activeIndex });
    setSelectedLineIndex(activeIndex + 1);
  }, [dispatch, activeIndex, setSelectedLineIndex]);

  /* ── Removing a line ────────────────────────────────────────────────────
   * There is no undo. A line an estimator has actually filled in — dimensions,
   * a material, a quoted price, hours typed by hand — is an afternoon's work
   * that one mis-aimed click used to delete without a word. So a line with
   * work on it asks first, and names what it is about to take.
   *
   * A blank row is not work: it goes straight away, because a confirmation on
   * an empty line is the kind of ceremony that teaches people to click through
   * confirmations without reading them.
   */
  const [confirmRemove, setConfirmRemove] = useState(false);

  // The rail's naming, so the dialog names the line the way the estimator has
  // been reading it: what they typed, else the product type, else what a
  // custom / one-off line is. Falling all the way through means the line has nothing
  // to be called yet and the position is the only honest label.
  const removeTargetName = useMemo(() => {
    if (!activeLine) return null;
    const typed = String(activeLine.name || "").trim();
    if (typed) return typed;
    const typeName = (formData?.availableProductTypes || []).find(
      (type) => type.key === activeLine.product_type_key,
    )?.display_name;
    if (typeName) return typeName;
    return activeLine.mode === "custom" ? "Custom / one-off" : null;
  }, [activeLine, formData?.availableProductTypes]);

  const removeTargetAmount = Number(activeResult?.calculated_total_cost);

  // Removing the LAST line used to leave the selection one past the end, and
  // the clamp above landed it on the job-level costs row — the estimator asked
  // to remove a line and got a different screen. Land on the nearest survivor.
  const removeLineAt = useCallback(
    (index) => {
      dispatch({ type: "REMOVE_LINE_ITEM", index });
      setSelectedLineIndex(Math.max(0, Math.min(index, lineItems.length - 2)));
    },
    [dispatch, lineItems.length, setSelectedLineIndex],
  );

  const handleRemoveLine = useCallback(() => {
    if (!activeLine || isBlankLineItem(activeLine)) {
      removeLineAt(activeIndex);
      return;
    }
    setConfirmRemove(true);
  }, [activeLine, activeIndex, removeLineAt]);

  const confirmRemoveLine = useCallback(() => {
    setConfirmRemove(false);
    removeLineAt(activeIndex);
  }, [activeIndex, removeLineAt]);

  const removeConfirmDialog = (
    <Dialog open={confirmRemove} onOpenChange={setConfirmRemove}>
      <DialogContent className="est-confirm">
        <DialogHeader>
          <DialogTitle>
            {removeTargetName
              ? `Remove "${removeTargetName}"?`
              : `Remove line ${activeIndex + 1}?`}
          </DialogTitle>
          <DialogDescription>
            {Number.isFinite(removeTargetAmount) && removeTargetAmount > 0
              ? `This line and its ${formatCurrency(removeTargetAmount)} come off the job. There is no undo.`
              : "This line comes off the job. There is no undo."}
          </DialogDescription>
        </DialogHeader>
        <div className="est-confirm__actions">
          <button
            type="button"
            className="wb-tbtn"
            onClick={() => setConfirmRemove(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="wb-tbtn est-confirm__danger"
            onClick={confirmRemoveLine}
          >
            Remove line
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const handleClearLaborOverride = useCallback(() => {
    dispatch({
      type: "CLEAR_LINE_ITEM_LABOR_OVERRIDE",
      index: activeIndex,
    });
  }, [dispatch, activeIndex]);

  const handleFocusLaborOverride = useCallback(() => {
    laborPanelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    laborPanelRef.current?.focus?.();
  }, []);

  const handleOpenTier = useCallback(() => {
    tierRef.current?.scrollIntoView?.({ block: "center" });
    tierRef.current?.querySelector("select")?.focus();
  }, []);

  const jobName =
    formData.job_name?.trim() || currentJob?.job_name || "Untitled Estimate";
  const customerName =
    formData.customer_name?.trim() || currentJob?.customer_name || "";
  /* Which version is on screen — and, when it is not the newest one, that it
     is not. An estimator editing a superseded version sees numbers that have
     already been replaced; saying so in the crumb is the difference between
     reading an old quote and mistaking it for the current one. */
  const latestVersionNumber = currentJob?.version_count ?? null;
  const isSupersededVersion =
    viewingVersionNumber != null &&
    latestVersionNumber != null &&
    viewingVersionNumber < latestVersionNumber;
  /* Short forms, because the crumb is the squeezed real estate: "v1 of 5", not
     "version 1 of 5". The full sentence lives in the notice strip. */
  const versionLabel =
    viewingVersionNumber != null
      ? isSupersededVersion
        ? `v${viewingVersionNumber} of ${latestVersionNumber}`
        : `v${viewingVersionNumber}`
      : currentJob?.id
        ? "latest version"
        : "unsaved";
  /* The customer name is the part that may be truncated; the version clause
     never is. A subtitle built by joining strings ellipsises at the END, so
     under pressure it died as "Pier House Marina Renov… · ver…" — losing the
     one thing the crumb is there to tell you. Two spans, one that shrinks and
     one that does not. */
  const subtitle = (
    <span className="est-crumb">
      {customerName ? (
        <span className="est-crumb__customer">{customerName}</span>
      ) : null}
      {customerName ? (
        <span className="est-crumb__sep" aria-hidden="true">
          ·
        </span>
      ) : null}
      <span
        className={`est-crumb__version${
          isSupersededVersion ? " est-crumb__stale" : ""
        }`}
      >
        {versionLabel}
      </span>
    </span>
  );

  /* The always-on carrier. One short sentence and the way out of the state:
     Load latest re-opens the job, which loads its current version — and goes
     through the guarded open handler, so a dirty form is asked about first. */
  const notice = isSupersededVersion ? (
    <div className="wb-notice wb-notice--warn" role="status">
      <span className="wb-notice__text">
        {`Viewing version ${viewingVersionNumber} of ${latestVersionNumber} — not the latest.`}
      </span>
      {onLoadLatest ? (
        <button
          type="button"
          className="wb-plain wb-notice__action"
          onClick={onLoadLatest}
        >
          Load latest
        </button>
      ) : null}
    </div>
  ) : null;

  const hint = nextActionTitle({
    hasDataIssues: Boolean(formData.materialsError),
    isDataLoading: Boolean(formData.materialsLoading),
    isCalculating,
    calculationError,
    results,
  });

  const extrasTotal =
    results &&
    results.pass_through_costs !== undefined &&
    results.pass_through_costs !== null
      ? formatCurrency(results.pass_through_costs)
      : null;

  // A pill that reads "Draft" on every estimate for a whole session states
  // the default. It earns its place only once the status is something the
  // workbench itself cannot change back — sent, won, lost.
  const status = formData.status || currentJob?.status || "draft";
  const meta = (
    <div className="est-meta" ref={tierRef}>
      {status !== "draft" ? (
        <span className="wb-pill">{formatStatus(status)}</span>
      ) : null}
      <TierControl formData={formData} dispatch={dispatch} />
    </div>
  );

  const actions = (
    <>
      <button
        type="button"
        className="wb-tbtn"
        onClick={onSaveClick}
        disabled={Boolean(formData.materialsLoading)}
      >
        Save
      </button>
      <button type="button" className="wb-tbtn" onClick={onNewJobClick}>
        New
      </button>
      {currentJob ? (
        <button
          type="button"
          className="wb-tbtn"
          onClick={onShowVersionHistory}
        >
          Versions
        </button>
      ) : null}
      <button
        type="button"
        className="wb-tbtn wb-tbtn--solid"
        onClick={onCalculate}
        disabled={isCalculating}
      >
        {isCalculating
          ? "Calculating…"
          : resultsStale && results
            ? "Recalculate"
            : "Calculate"}
      </button>
    </>
  );

  // A segment, not a toggle. The old pair said "Cost trace · this line" in the
  // header and "Whole job" on the button — the label named where you were and
  // the control named where you would go, so half the chrome was a riddle.
  // Both scopes are visible now and the lit one is the one you are in.
  const scopeToggle = (
    <div className="wb-segment" role="group" aria-label="Cost trace scope">
      <button
        type="button"
        className={`wb-plain wb-segment__btn${
          traceScope === "line" ? " wb-segment__btn--on" : ""
        }`}
        aria-pressed={traceScope === "line"}
        onClick={() => setTraceScope("line")}
      >
        This line
      </button>
      <button
        type="button"
        className={`wb-plain wb-segment__btn${
          traceScope === "job" ? " wb-segment__btn--on" : ""
        }`}
        aria-pressed={traceScope === "job"}
        onClick={() => setTraceScope("job")}
      >
        Whole job
      </button>
    </div>
  );

  const center = (
    <>
      {formData.materialsError ? (
        <p className="wb-empty wb-empty--error">{formData.materialsError}</p>
      ) : null}
      {calculationError ? (
        <p className="wb-empty wb-empty--error" role="alert">
          Calculation error: {calculationError}
        </p>
      ) : null}
      {showingExtras ? (
        <JobCostsEditor formData={formData} dispatch={dispatch} />
      ) : (
        <LineItemEditor
          // One editor per line: disclosures only ever open from props, so
          // a reused instance would carry line A's open Quoted price / child
          // cards onto line B. The revision changes whenever lines are added
          // or removed, so a line sliding into this slot remounts too, and the
          // job / version in view keeps line 0 of job A from reusing job B's.
          key={`${currentJob?.id ?? "new"}-${viewingVersionNumber ?? "latest"}-${activeIndex}-${formData.line_items_revision || 0}`}
          lineItem={activeLine}
          lineIndex={activeIndex}
          dispatch={dispatch}
          availableProductTypes={formData.availableProductTypes}
          availableMaterials={formData.availableMaterials}
          result={activeResult}
          formData={formData}
          laborPanelRef={laborPanelRef}
          isAdmin={isAdmin}
          draft={activeDraft}
          onAcceptDraft={handleAcceptDraft}
          onDiscardDraft={handleDiscardDraft}
          candidateState={
            activeDraftId ? candidateStateById[activeDraftId] || null : null
          }
          onRetryCandidate={handleRetryCandidate}
          onFileQuotedPrice={handleFileQuotedPrice}
          quotedPriceFileState={quotedPriceStateByIndex[activeIndex] || null}
        />
      )}
    </>
  );

  const centerHeader = showingExtras
    ? { label: "Job-level costs" }
    : {
        label: `Line ${activeIndex + 1} of ${lineItems.length}`,
        action: (
          <>
            <button
              type="button"
              className="wb-tbtn"
              onClick={handleDuplicateLine}
            >
              Duplicate
            </button>
            <button
              type="button"
              className="wb-tbtn"
              onClick={handleRemoveLine}
              disabled={lineItems.length <= 1}
              // Only when disabled: a tooltip on a working button explains
              // nothing, but a button that refuses to click owes a reason.
              title={
                lineItems.length <= 1
                  ? "A job needs at least one line"
                  : undefined
              }
            >
              Remove
            </button>
          </>
        ),
      };

  return (
    <>
      <ComposerDialog
        open={composerOpen}
        onOpenChange={setComposerOpen}
        onDrafted={handleDrafted}
        availableMaterials={formData.availableMaterials}
        onUnavailable={composer.markUnavailable}
      />
      {removeConfirmDialog}
      <WorkbenchShell
        variant="estimate"
        title={jobName}
        subtitle={subtitle}
        notice={notice}
        meta={meta}
        actions={actions}
        railHeader={{
          label: "Job contents",
          count: lineItems.length,
          action: (
            <button
              type="button"
              className="wb-tbtn"
              onClick={handleAddLine}
              aria-label="Add line item"
              title="Add line item"
            >
              + Add
            </button>
          ),
        }}
        rail={
          <JobContentsRail
            lineItems={lineItems}
            results={results}
            availableProductTypes={formData.availableProductTypes}
            selectedIndex={activeIndex}
            onSelect={setSelectedLineIndex}
            onAddLine={handleAddLine}
            extrasIndex={extrasIndex}
            extrasSummary={extrasTotal}
            composerAvailable={composer.available}
            onDraftWithAi={() => setComposerOpen(true)}
          />
        }
        centerHeader={centerHeader}
        center={center}
        inspectorHeader={{ label: "Cost trace", action: scopeToggle }}
        inspector={
          <CostTraceInspector
            scope={showingExtras ? "job" : traceScope}
            item={activeResult}
            lineIndex={activeIndex}
            results={results}
            formData={formData}
            onClearLaborOverride={handleClearLaborOverride}
            onFocusLaborOverride={handleFocusLaborOverride}
            onOpenTier={handleOpenTier}
            draft={showingExtras ? null : activeDraft}
            resultsStale={resultsStale}
          />
        }
        statusBar={
          <EstimateStatusStrip
            results={results}
            resultsStale={resultsStale}
            hint={hint}
            formData={formData}
          />
        }
      />
    </>
  );
}

export default EstimateWorkbench;
